import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ReportsProvider } from './contexts/ReportsContext';
import { apiClient } from './utils/api';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy load components for code splitting
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const ReportDetailPage = React.lazy(() => import('./pages/ReportDetailPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const ReportEditor = React.lazy(() => import('./pages/ReportEditor'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const PhotoGallery = React.lazy(() => import('./pages/PhotoGallery'));
const PhotoManagement = React.lazy(() => import('./pages/PhotoManagement'));
const VideosPage = React.lazy(() => import('./pages/VideosPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const BlogManagementPage = React.lazy(() => import('./pages/admin/BlogManagementPage'));
const BlogEditorPage = React.lazy(() => import('./pages/admin/BlogEditorPage'));
const VideoManagementPage = React.lazy(() => import('./pages/admin/VideoManagementPage'));
const VideoEditorPage = React.lazy(() => import('./pages/admin/VideoEditorPage'));
const DorothyPage = React.lazy(() => import('./pages/DorothyPage'));
const TelemetryPage = React.lazy(() => import('./pages/admin/TelemetryPage'));
const DorothyAlertsManagementPage = React.lazy(() => import('./pages/admin/DorothyAlertsManagementPage'));
const DorothyAlertEditorPage = React.lazy(() => import('./pages/admin/DorothyAlertEditorPage'));

const AppRoutes: React.FC = () => {
  const { tokens, refreshToken } = useAuth();

  // Set up API client with auth handlers
  // Read token from localStorage to avoid stale closure issues during token refresh.
  // When refreshToken() dispatches new tokens, React state hasn't updated yet,
  // but localStorage IS updated synchronously (AuthContext.tsx line 253).
  useEffect(() => {
    apiClient.setAuthHandlers(
      () => {
        try {
          const stored = localStorage.getItem('mampd_auth_tokens');
          if (stored) {
            const parsed = JSON.parse(stored);
            return parsed.accessToken || null;
          }
        } catch {
          // ignore parse errors
        }
        return tokens?.accessToken || null;
      },
      async () => {
        return await refreshToken();
      }
    );
  }, [tokens, refreshToken]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/informes/:reportId" element={<ReportDetailPage />} />
      <Route path="/fotos" element={<PhotoGallery />} />
      <Route path="/videos" element={<VideosPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:postId" element={<BlogPostPage />} />
      <Route path="/dorothy" element={<DorothyPage />} />
      <Route path="/backadmin" element={<LoginPage />} />
      
      {/* Protected admin routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/reports" 
        element={
          <ProtectedRoute>
            <ReportEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/reports/:id" 
        element={
          <ProtectedRoute>
            <ReportEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/photos" 
        element={
          <ProtectedRoute>
            <PhotoManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog" 
        element={
          <ProtectedRoute>
            <BlogManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog/new" 
        element={
          <ProtectedRoute>
            <BlogEditorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog/:postId" 
        element={
          <ProtectedRoute>
            <BlogEditorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog/:postId/edit" 
        element={
          <ProtectedRoute>
            <BlogEditorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/videos" 
        element={
          <ProtectedRoute>
            <VideoManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/videos/new" 
        element={
          <ProtectedRoute>
            <VideoEditorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/videos/:videoId" 
        element={
          <ProtectedRoute>
            <VideoEditorPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/videos/:videoId/edit"
        element={
          <ProtectedRoute>
            <VideoEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/telemetry"
        element={
          <ProtectedRoute>
            <TelemetryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dorothy/alerts/new"
        element={
          <ProtectedRoute>
            <DorothyAlertEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dorothy/alerts/:alertId/edit"
        element={
          <ProtectedRoute>
            <DorothyAlertEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dorothy/alerts"
        element={
          <ProtectedRoute>
            <DorothyAlertsManagementPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ReportsProvider>
          <Router>
            <Layout>
              <Suspense fallback={<LoadingSpinner />}>
                <AppRoutes />
              </Suspense>
            </Layout>
          </Router>
        </ReportsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;